import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Compass, BookOpen, Layers, ShieldCheck, Mail, Map, FileText, ArrowUpRight, HelpCircle, MessageSquare } from 'lucide-react';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { BLOG_POSTS } from '@/data/blog-posts';
import { TUTORIALS } from '@/data/tutorials';
import { GUIDE_CATEGORIES } from '@/data/guides';

const SitemapPage: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const }
    }
  };

  // Structured Data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://funcirclegames.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Sitemap",
        "item": "https://funcirclegames.com/sitemap"
      }
    ]
  };

  return (
    <div className="relative min-h-screen pt-32 pb-24 overflow-x-hidden bg-white">
      <SEO 
        title="Site Map | Fun Circle Games"
        description="Navigate all public pages, tools, tutorials, updates, and resources on the Fun Circle Games platform. Find what you need quickly."
        keywords="sitemap, Fun Circle Games pages, all links, navigation hub, ball simulation resources"
        canonical="/sitemap"
      />
      
      <BackgroundAnimation opacity={0.3} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] as const }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-50 border border-zinc-100 mb-6 font-sans text-[10px] uppercase tracking-[0.3em] text-zinc-500 mx-auto">
            <Map className="w-3.5 h-3.5" />
            <span>Navigation Hub</span>
          </div>
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-6 uppercase tracking-tighter leading-none">
            Site <span className="text-zinc-400">Map</span>
          </h1>
          <p className="text-zinc-500 text-lg max-w-xl mx-auto font-sans">
            A comprehensive index of all public pages, marketing tools, legal documentation, blog entries, and education guides.
          </p>
        </motion.div>

        {/* Categories Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {/* Category: Product */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-cyan-50 text-cyan-600">
                <Compass className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Product & Platform</h2>
            </div>
            <ul className="space-y-4">
              <li>
                <Link to="/" className="text-zinc-600 hover:text-cyan-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Home / Platform Overview</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="text-zinc-600 hover:text-cyan-600 transition-colors font-medium flex items-center justify-between group">
                  <span>How It Works</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/plans" className="text-zinc-600 hover:text-cyan-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Pricing & Membership Plans</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/showcase" className="text-zinc-600 hover:text-cyan-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Showcase & Demos</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-zinc-600 hover:text-cyan-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Help Center & FAQ</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Category: Landing Pages */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600">
                <Layers className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Featured Landing Pages</h2>
            </div>
            <ul className="space-y-4">
              <li>
                <Link to="/ball-simulation" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Ball Simulation Studio</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/physics-video-generator" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Physics Video Generator</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/asmr-video-generator" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>ASMR Video Generator</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/faceless-video-generator" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Faceless Channel Video Maker</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/youtube-shorts-generator" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>YouTube Shorts Video Maker</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/vs-blender" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Compare: VS Blender</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/vs-after-effects" className="text-zinc-600 hover:text-indigo-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Compare: VS After Effects</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Category: Company */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-amber-50 text-amber-600">
                <Mail className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Company & Contact</h2>
            </div>
            <ul className="space-y-4">
              <li>
                <Link to="/about" className="text-zinc-600 hover:text-amber-600 transition-colors font-medium flex items-center justify-between group">
                  <span>About Our Brand</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-zinc-600 hover:text-amber-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Get In Touch</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/case-studies" className="text-zinc-600 hover:text-amber-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Creator Success Stories</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/updates" className="text-zinc-600 hover:text-amber-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Stay Synchronized (Updates)</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Category: Legal Docs */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-emerald-50 text-emerald-600">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Legal & Terms</h2>
            </div>
            <ul className="space-y-4">
              <li>
                <Link to="/privacy-policy" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Privacy Policy</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Terms of Service</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Cookie Policy</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/refund-policy" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Refund Policy</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/copyright-policy" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Copyright & DMCA Policy</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/acceptable-use" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Acceptable Use Policy</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link to="/beta-terms" className="text-zinc-600 hover:text-emerald-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Beta Testing Terms</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </motion.div>

          {/* Category: Educational & Tutorials */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-purple-50 text-purple-600">
                <BookOpen className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Tutorials & Guides</h2>
            </div>
            <ul className="space-y-4 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
              <li>
                <Link to="/tutorials" className="text-zinc-900 hover:text-purple-600 transition-colors font-semibold flex items-center justify-between group">
                  <span>All Tutorials Hub</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              {TUTORIALS.map((tutorial) => (
                <li key={tutorial.slug}>
                  <Link to={`/tutorials/${tutorial.slug}`} className="text-zinc-500 hover:text-purple-600 transition-colors text-sm flex items-center justify-between group">
                    <span className="truncate max-w-[200px]">{tutorial.title}</span>
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Category: Articles & News */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-rose-50 text-rose-600">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Blog Posts & Insights</h2>
            </div>
            <ul className="space-y-4 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
              <li>
                <Link to="/blog" className="text-zinc-900 hover:text-rose-600 transition-colors font-semibold flex items-center justify-between group">
                  <span>All Blog Posts</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              {BLOG_POSTS.map((post) => (
                <li key={post.slug}>
                  <Link to={`/blog/${post.slug}`} className="text-zinc-500 hover:text-rose-600 transition-colors text-sm flex items-center justify-between group">
                    <span className="truncate max-w-[200px]">{post.title}</span>
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Category: Help Center Guides */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-cyan-50 text-cyan-600">
                <HelpCircle className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Help Center Guides</h2>
            </div>
            <ul className="space-y-4 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
              <li>
                <Link to="/help" className="text-zinc-950 hover:text-cyan-600 transition-colors font-semibold flex items-center justify-between group">
                  <span>Help Center Main</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              {GUIDE_CATEGORIES.map((category) => (
                <React.Fragment key={category.id}>
                  <li className="text-[9px] font-black uppercase tracking-widest text-zinc-400 mt-4">
                    {category.title}
                  </li>
                  {category.articles.map((article) => (
                    <li key={article.slug}>
                      <Link to={`/help/${category.id}/${article.slug}`} className="text-zinc-500 hover:text-cyan-600 transition-colors text-sm flex items-center justify-between group pl-2 border-l border-zinc-100">
                        <span className="truncate max-w-[200px]">{article.title}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </Link>
                    </li>
                  ))}
                </React.Fragment>
              ))}
            </ul>
          </motion.div>

          {/* Category: Community & Socials */}
          <motion.div variants={itemVariants} className="p-8 rounded-[2rem] border border-zinc-200 bg-white/80 backdrop-blur-md shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-2xl bg-blue-50 text-blue-600">
                <MessageSquare className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-900">Community & Channels</h2>
            </div>
            <ul className="space-y-4">
              <li>
                <a href="https://discord.gg/cdwNFdbx" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-blue-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Official Discord Server</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
              <li>
                <a href="https://youtube.com/@funcirclegames" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-red-600 transition-colors font-medium flex items-center justify-between group">
                  <span>YouTube Channel</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
              <li>
                <a href="https://instagram.com/funcirclegames" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-pink-600 transition-colors font-medium flex items-center justify-between group">
                  <span>Instagram Profile</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
              <li>
                <a href="https://snapchat.com/add/funcirclegames" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-amber-500 transition-colors font-medium flex items-center justify-between group">
                  <span>Snapchat Profile</span>
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Technical Footer Links block */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 pt-8 border-t border-zinc-100 text-center"
        >
          <p className="text-zinc-400 text-xs font-mono">
            Technical Resources: 
            <a href="/sitemap.xml" className="text-zinc-600 hover:text-cyan-600 mx-2 transition-colors">sitemap.xml</a> | 
            <a href="/robots.txt" className="text-zinc-600 hover:text-cyan-600 mx-2 transition-colors">robots.txt</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SitemapPage;
